import { getProp } from "../ParsedSummary";
import { collaborationLevelDescriptions } from "./questionBreakdown";
import { questionRef } from "./quotes";
import { teamworkLevelLabel } from "utils/teamworkLevel";
import { SOLO_ACCURACY_TITLE, SOLO_ACCURACY_RULE } from "utils/soloAccuracy";
import { sessionSurveyLabel } from "utils/sessionSurvey";

export const NO_SURVEY_RESPONSE = 'No Response';

/**
 * What the End Of Session Survey bullet reads.
 *
 * The backend's record of what the student pressed comes first. Reports
 * generated before v9 — which is what production serves today — carry only the
 * model's own field, so that remains the fallback rather than a regression to
 * "No Response". A rating this build cannot name falls through the same way.
 *
 * @param {string|null|undefined} backendRating The raw enum from the session row.
 * @param {*} modelAnswer Whatever the model wrote, which may be anything.
 * @returns {string} The bullet's content, never a raw enum token.
 */
export const surveyAnswer = (backendRating, modelAnswer) => sessionSurveyLabel(backendRating)
  || (modelAnswer && typeof modelAnswer === 'string' ? modelAnswer : NO_SURVEY_RESPONSE);

// The eight Standards for Mathematical Practice. The rubric the model emits is
// SPARSE — it rates only what it saw evidence for — and absence IS the value
// "Insufficient Evidence", which the renderer supplies and the model never
// writes. An empty object is valid and common.
const MP_PRACTICES = ['MP1', 'MP2', 'MP3', 'MP4', 'MP5', 'MP6', 'MP7', 'MP8'];
const INSUFFICIENT_EVIDENCE = 'Insufficient Evidence';

const mpRubricSection = (student, sections) => {
  const rubric = getProp(student, ['mpRubricResults', 'mp_rubric_results']);
  if (!rubric || typeof rubric !== 'object' || Array.isArray(rubric)) return;

  MP_PRACTICES.forEach((practice) => {
    const rating = rubric[practice];
    sections.push({
      type: 'bullet',
      label: `${practice}:`,
      content: typeof rating === 'string' && rating ? rating : INSUFFICIENT_EVIDENCE
    });
  });
};

// The collaboration Top Quote, as a pre-cutoff report has always written it:
// the speaker names the bullet, and the question is inlined with a Q the caller
// prefixes whether or not the payload already carries one — "(QQ2)" when it
// does. Kept verbatim, defect included, because a pre-cutoff report may not
// change; questionRef is the fix and it lives on the gated bullet.
const legacyCollaborationQuote = (quoteText, qNum, speaker) => ({
  type: 'bullet',
  label: speaker && typeof speaker === 'string' ? `${speaker}:` : 'Top Quote:',
  content: `"${quoteText}"${qNum ? ` (Q${qNum})` : ''}`
});

// The leader names the bullet, not the speaker: this is the student's own
// section and the bullet is read for what it is.
const gatedCollaborationQuote = (quoteText, qNum) => ({
  type: 'bullet',
  label: 'Top Quote:',
  content: `"${quoteText}"${questionRef(qNum)}`
});

// The rubric rows do not reach a gated Student Detail at all: the GLOW / GROW
// spotlight below them is the section a teacher reads about practices.
const noMpRubricSection = () => {};

// The words behind the strength. Pre-cutoff they sit under the GLOW headline
// with nothing naming them; gated, the leader says what they are doing there.
const legacyGlowEvidence = (quote) => ({
  type: 'sub-bullet',
  content: `"${quote}"`
});

const gatedGlowEvidence = (quote) => ({
  type: 'sub-bullet',
  label: 'Evidence:',
  content: `"${quote}"`
});

// The two Student Detail shapes, picked once from the gate rather than three
// times inside the walk. A pre-cutoff session renders LEGACY and must go on
// rendering it unchanged; a gated session renders GATED.
const LEGACY_STUDENT_DETAIL = {
  mpRubricSection,
  collaborationQuote: legacyCollaborationQuote,
  glowEvidence: legacyGlowEvidence
};

const GATED_STUDENT_DETAIL = {
  mpRubricSection: noMpRubricSection,
  collaborationQuote: gatedCollaborationQuote,
  glowEvidence: gatedGlowEvidence
};

export const getStudentList = (summary) => {
  const studentData = getProp(summary, ['student', 'performance']);
  const list = Array.isArray(studentData) ? studentData : getProp(studentData, ['summaries', 'summary']);
  return (list && Array.isArray(list) && list.length > 0) ? list : [];
};

export const studentExecutiveSummary = (summary, sections, isShortSession = false, currentStudentIndex, deterministic = {}) => {
  const studentList = getStudentList(summary);
  const detail = deterministic.enabled ? GATED_STUDENT_DETAIL : LEGACY_STUDENT_DETAIL;

  if (studentList.length > 0) {
    const studentsToRender = currentStudentIndex !== undefined
      ? [{ student: studentList[currentStudentIndex], index: currentStudentIndex }]
      : studentList.map((student, index) => ({ student, index }));

    studentsToRender.forEach(({ student, index }) => {
      const studentName = getProp(student, ['name', 'student']);

      const mathLearning = getProp(student, ['math', 'learning']);
      if (mathLearning) {
        const mathTitle = getProp(mathLearning, 'title');
        sections.push({
          type: 'subtitle',
          content: mathTitle || 'Math Learning'
        });

        // The score is the first thing read: the Solo Time fraction sits above
        // the Headline, matching the collaboration section below.
        const studentId = getProp(student, ['student_id', 'id']);
        const soloPill =
          deterministic.soloAccuracy?.[
            String(studentName || '').toLowerCase()
          ] ||
          (studentId
            ? deterministic.soloAccuracy?.[String(studentId).toLowerCase()]
            : undefined);
        if (soloPill && !isShortSession) {
          if (soloPill.notApplicable || soloPill.not_applicable) {
            sections.push({
              type: 'bullet',
              label: SOLO_ACCURACY_TITLE,
              content:
                soloPill.notApplicableReason ||
                soloPill.not_applicable_reason ||
                'Not applicable',
            });
          } else {
            sections.push({
              type: 'bullet',
              // A title like "Headline:" or "Focus:", with only the numbers inside
              // the pill beside it.
              label: SOLO_ACCURACY_TITLE,
              pillText: soloPill.fraction,
              pill: soloPill.tone,
              tooltip: SOLO_ACCURACY_RULE,
            });
          }
        }

        const mathHeadline = getProp(mathLearning, 'headline');
        if (mathHeadline && typeof mathHeadline === 'string') {
          sections.push({
            type: 'bullet',
            label: 'Headline:',
            content: mathHeadline
          });
        }

        // Skip other details for short sessions
        if (!isShortSession) {
          const category = soloPill ? null : getProp(mathLearning, 'category');
          if (category && typeof category === 'string') {
            // Remove duplicate "Math" prefix if data already contains it (e.g. "Math Math Level 2")
            const cleanCategory = category.replace(/^Math\s+Math\s/i, 'Math ');
            const categoryParts = cleanCategory.split(' - ');
            if (categoryParts.length > 1) {
              sections.push({
                type: 'bullet',
                label: `${categoryParts[0]}:`,
                content: categoryParts.slice(1).join(' - ')
              });
            } else {
              sections.push({
                type: 'bullet',
                content: category
              });
            }
          }

          const commentary = getProp(mathLearning, 'commentary');
          if (commentary && typeof commentary === 'string') {
            sections.push({
              type: 'bullet',
              content: commentary
            });
          }

          // Exact key first: newer reports add `recommendationTask` beside it,
          // which the substring lookup would otherwise match.
          const recommendation = mathLearning.recommendation ?? getProp(mathLearning, 'recommendation');
          if (recommendation && typeof recommendation === 'string') {
            sections.push({
              type: 'bullet',
              label: 'Recommendation:',
              content: recommendation
            });
          }

          const backendRating = deterministic.endOfSessionSurvey?.[
            String(studentName || '').toLowerCase()
          ];
          const endOfSessionSurvey = getProp(mathLearning, ['endOfSessionSurvey', 'end_of_session_survey']);
          sections.push({
            type: 'bullet',
            label: 'End Of Session Survey:',
            content: surveyAnswer(backendRating, endOfSessionSurvey)
          });

          const colorCode = getProp(mathLearning, ['color', 'code']);
          if (colorCode && typeof colorCode === 'string') {
            sections.push({
              type: 'bullet',
              content: colorCode
            });
          }

          const misconception = getProp(mathLearning, 'misconception');
          if (misconception && misconception !== '' && typeof misconception === 'string') {
            sections.push({
              type: 'bullet',
              content: misconception
            });
          }

          const misconceptionQuote = getProp(mathLearning, ['misconception_quote', 'quote']);
          if (misconceptionQuote) {
            const quoteList = Array.isArray(misconceptionQuote) ? misconceptionQuote : [misconceptionQuote];
            if (quoteList.length > 0 && quoteList[0] !== '') {
              sections.push({
                type: 'bullet',
                content: 'Misconception Quotes:'
              });
              quoteList.forEach(quote => {
                if (quote && quote !== '' && typeof quote === 'string') {
                  sections.push({
                    type: 'bullet',
                    content: `"${quote}"`
                  });
                }
              });
            }
          }
        }
      }

      const collaboration = getProp(student, ['collaboration', 'participation']);
      if (collaboration) {
        const collabTitle = getProp(collaboration, 'title');
        sections.push({
          type: 'subtitle',
          content: collabTitle || 'Collaboration & Participation'
        });

        // The backend's collaboration score is relabelled and sits above the
        // Headline, which the LLM still writes.
        const backendLevel = deterministic.studentCollabLevels?.[
          String(studentName || '').toLowerCase()
        ];
        const backendLabel = teamworkLevelLabel(backendLevel);
        if (backendLabel && !isShortSession) {
          sections.push({
            type: 'bullet',
            label: backendLabel,
            content: '',
            tooltip: collaborationLevelDescriptions[String(backendLevel)],
            tooltipAfterContent: true
          });
        }

        const collabHeadline = getProp(collaboration, 'headline');
        if (collabHeadline && typeof collabHeadline === 'string') {
          sections.push({
            type: 'bullet',
            label: 'Headline:',
            content: collabHeadline
          });
        }

        // Skip other details for short sessions
        if (!isShortSession) {
          const level = backendLabel ? null : getProp(collaboration, ['level', 'category']);
          if (level && typeof level === 'string') {
            const levelNum = String(level).match(/\d+/)?.[0];
            const levelParts = level.split(' - ');
            if (levelParts.length > 1) {
              const levelLabel = /^level\s/i.test(levelParts[0]) ? levelParts[0] : `Level ${levelParts[0]}`;
              sections.push({
                type: 'bullet',
                label: levelLabel,
                content: levelParts.slice(1).join(' - '),
                tooltip: levelNum ? collaborationLevelDescriptions[levelNum] : undefined,
                tooltipAfterContent: true
              });
            } else {
              sections.push({
                type: 'bullet',
                content: level,
                tooltip: levelNum ? collaborationLevelDescriptions[levelNum] : undefined
              });
            }
          }

          const topQuote = getProp(collaboration, ['topQuote', 'top_quote', 'supporting_quote', 'quote', 'top']);
          if (topQuote) {
            const quoteText = typeof topQuote === 'string' ? topQuote : getProp(topQuote, 'quote');
            const qNum = getProp(topQuote, ['qNum', 'qnum', 'question']);

            if (quoteText && quoteText !== 'N/A' && quoteText !== '' && typeof quoteText === 'string') {
              const speaker = typeof topQuote === 'string' ? undefined : getProp(topQuote, 'student');
              sections.push(detail.collaborationQuote(quoteText, qNum, speaker));
            }
          }

          const collabCommentary = getProp(collaboration, 'commentary');
          if (collabCommentary && typeof collabCommentary === 'string') {
            sections.push({
              type: 'bullet',
              content: collabCommentary
            });
          }

          const colorCode = getProp(collaboration, ['color', 'code']);
          if (colorCode && typeof colorCode === 'string') {
            sections.push({
              type: 'bullet',
              content: colorCode
            });
          }

          const collabRecommendation = getProp(collaboration, 'recommendation');
          if (collabRecommendation && typeof collabRecommendation === 'string') {
            sections.push({
              type: 'bullet',
              label: 'Recommendation:',
              content: collabRecommendation
            });
          }
        }
      }

      // Skip Key Mathematical Practice Spotlight for short sessions
      if (!isShortSession) {
        detail.mpRubricSection(student, sections);

        const spotlight = getProp(student, ['spotlight', 'mp']);
        if (spotlight) {
          const spotlightTitle = getProp(spotlight, 'title');
          sections.push({
            type: 'subtitle',
            content: spotlightTitle || 'Key Mathematical Practice Spotlight'
          });

          // Helper to check if value is empty or null-like string
          const isEmptyOrNull = (value) => {
            if (!value) return true;
            const str = String(value).trim().toLowerCase();
            return str === '' || str === 'null' || str === 'undefined' || str === 'none';
          };

          const glow = getProp(spotlight, 'glow');
          const glowContent = getProp(glow, ['headline', 'commentary']);
          const glowLabelRaw = getProp(glow, 'label');
          // Ensure GLOW label has proper prefix, treat "null" string as empty
          const glowLabel = !isEmptyOrNull(glowLabelRaw)
            ? (glowLabelRaw.toLowerCase().startsWith('glow')
              ? glowLabelRaw
              : `GLOW (Strength) - '${glowLabelRaw}'`)
            : 'GLOW (Strength)';
          sections.push({
            type: 'bullet',
            label: `${glowLabel}:`,
            content: typeof glowContent === 'string' && !isEmptyOrNull(glowContent) ? glowContent : 'none'
          });

          // The evidence behind the strength. Sits under its headline so the
          // claim and the words that back it read together, and is named so the
          // quote does not read as a stray line.
          const glowQuote = getProp(glow, 'quote');
          if (typeof glowQuote === 'string' && !isEmptyOrNull(glowQuote)) {
            sections.push(detail.glowEvidence(glowQuote));
          }

          const grow = getProp(spotlight, 'grow');
          const growContent = getProp(grow, ['headline', 'commentary']);
          const growLabelRaw = getProp(grow, 'label');
          // Ensure GROW label has proper prefix, treat "null" string as empty
          const growLabel = !isEmptyOrNull(growLabelRaw)
            ? (growLabelRaw.toLowerCase().startsWith('grow')
              ? growLabelRaw
              : `GROW (Opportunity) - '${growLabelRaw}'`)
            : 'GROW (Opportunity)';
          sections.push({
            type: 'bullet',
            label: `${growLabel}:`,
            content: typeof growContent === 'string' && !isEmptyOrNull(growContent) ? growContent : 'none'
          });
        }
      }
    });
  }
};
